import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import * as OrganizationActions from '@states/organization/organization.actions';
import * as SharedActions from '@states/shared/shared.actions';
import * as InviteActions from '@states/invite/invite.actions';
import { Observable, take } from 'rxjs';
import * as OrganizationSelectors from '@states/organization/organization.selectors';
import * as InviteSelectors from '@states/invite/invite.selectors';
import { ActiveOrganization, LiveViewType, LocalLiveViewType } from '@models/organization.model';
import { InviteWithOrgName } from '@models/invite.model';
import { Router } from '@angular/router';
import * as SharedSelectors from '@states/shared/shared.selectors';
import { ofType } from '@ngrx/effects';
import { SharedEffects } from '@effects/shared.effects';
import { routerSegments } from '@consts/routes';
import { OrganizationEffect } from '@effects/organization.effect';
import { Auth0Service } from '../../authentication/auth0.service';
import { PreloaderColor } from '@enums/shared.enum';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import { PermissionModel } from '@models/permission.model';
import { Quality } from '../../shared/ui-kit/ui-quality-selector/quality-selector.component';
import { UserSelectors } from '@states/user/user.selector-types';
import { LetDirective, PushPipe } from '@ngrx/component';
import { NgForOf, NgIf } from '@angular/common';
import { UiUserSettingsDateComponent } from '../../shared/ui-kit/ui-user-settings-date/ui-user-settings-date.component';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { UiPreloaderComponent } from '../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { UiPrefixInputComponent, UiAvatarComponent, UiCopyClipboardComponent, UiToggleComponent } from "@ui";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-organization-settings',
  templateUrl: './organization-settings.component.html',
  styleUrls: ['./organization-settings.component.scss'],
  imports: [
    LetDirective,
    NgIf,
    NgForOf,
    UiUserSettingsDateComponent,
    UiAvatarComponent,
    ReactiveFormsModule,
    UiPrefixInputComponent,
    MatSelect,
    UiToggleComponent,
    MatOption,
    UiCopyClipboardComponent,
    PushPipe,
    UiPreloaderComponent,
    MatCheckbox,
  ],
})
export class OrganizationSettingsComponent implements OnInit {

  public LiveViewType = LiveViewType;

  public selectIsDefaultOrganization$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.selectIsDefaultOrganization));

  public selectActiveOrganization$: Observable<ActiveOrganization> = this.store$.pipe(
    select(OrganizationSelectors.selectActiveOrganization),
  );

  public selectIsDeveloper$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDeveloper));

  public selectInvites$: Observable<InviteWithOrgName[]> = this.store$.pipe(select(InviteSelectors.selectInvites));

  public selectIsLoading$: Observable<boolean> = this.store$.pipe(select(SharedSelectors.selectIsLoading));


  public emails: UntypedFormArray = new UntypedFormArray([new UntypedFormControl('')]);

  public permissions: UntypedFormArray = new UntypedFormArray([new UntypedFormControl('read')]);
  public mobileLocalLiveViewTypes = LocalLiveViewType;
  public webLocalLiveViewTypes = LocalLiveViewType;
  public organizationForm: UntypedFormGroup;
  public step1Url: string;
  public isAcceptingEnd = true;
  public preloaderColor = PreloaderColor;
  public quality = Quality;
  private orgId: string;

  constructor(
    private store$: Store<AppState>,
    private router: Router,
    private sharedEffects$: SharedEffects,
    private organizatioEffects$: OrganizationEffect,
    private auth0Service: Auth0Service,
    private destroyRef: DestroyRef,
  ) { }

  ngOnInit(): void {

    this.organizatioEffects$.sendAcceptInvite$.pipe(takeUntilDestroyed(this.destroyRef), ofType(InviteActions.sendAcceptSuccess))
      .subscribe(res => {
        if (this.orgId) {
          this.auth0Service.switchTenant(this.orgId);
        }
      });

    this.selectIsDefaultOrganization$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (!this.orgId) {
          if (res) {
            this.step1Url = `${routerSegments.selectOrganization}/create-organization/step1`;
          } else {
            this.step1Url = 'organization/create-organization/step1';
          }
        }
      });

    this.organizationForm = new UntypedFormGroup({
      orgName: new UntypedFormControl('', [Validators.required]),
      orgEmail: new UntypedFormControl('', [Validators.email, Validators.required]),
      orgId: new UntypedFormControl('', [Validators.required]),
      mfa: new UntypedFormGroup({
        enabled: new UntypedFormControl(false, [Validators.required]),
      }),
      showCores: new UntypedFormControl(true),
      videoQuality: new UntypedFormControl(Quality.SQ),
      localLiveStream: new UntypedFormControl(false),
      webSocketPlayback: new UntypedFormControl(false),
      violenceEventEnabled: new UntypedFormControl(false),
      forceWebrtcRelay: new UntypedFormControl(false),
      liveViewType: new UntypedFormControl(LiveViewType.Webrtc),
      mobileLocalLiveViewType: new UntypedFormControl(LocalLiveViewType.Hls),
      webLocalLiveViewType: new UntypedFormControl(LocalLiveViewType.Webrtc),
    });

    this.store$.dispatch(OrganizationActions.getOrganizationUsers());
    this.store$.dispatch(InviteActions.getInvitesByOrgId());
    // this.store$.dispatch(InviteActions.getActiveInvites());
    this.selectActiveOrganization$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (res) {
          const mfaForm = this.organizationForm.get('mfa') as UntypedFormGroup;
          mfaForm.patchValue(res.mfa);
          this.organizationForm.patchValue({
            orgName: res.orgName,
            orgEmail: res.orgEmail,
            orgId: res.orgId,
            mfa: mfaForm,
            videoQuality: res?.videoQuality ?? Quality.SQ,
            localLiveStream: res?.localLiveStream ?? false,
            webSocketPlayback: res?.webSocketPlayback ?? false,
            violenceEventEnabled: res?.violenceEventEnabled ?? false,
            forceWebrtcRelay: res?.forceWebrtcRelay ?? false,
            liveViewType: res?.liveViewType ?? LiveViewType.Webrtc,
            showCores: res?.showCores ?? true,
            mobileLocalLiveViewType: res.mobileLocalLiveViewType ?? LocalLiveViewType.Hls,
            webLocalLiveViewType: res.mobileLocalLiveViewType ?? LocalLiveViewType.Webrtc,
          });
        }
      });


    this.sharedEffects$.confirmation$.pipe(takeUntilDestroyed(this.destroyRef), ofType(SharedActions.showConfirmModalResultConfirm))
      .subscribe(res => {
        this.store$.dispatch(
          OrganizationActions.removeUser({
            userId: res['params']['userId'],
          }),
        );
      });

    this.sharedEffects$.pressSave$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.save();
      });
  }

  public nextPageUsers(ev) {
    this.store$.dispatch(
      OrganizationActions.setPaginationParams({
        page: ev.pageIndex,
        perPage: ev.pageSize,
      }),
    );
    this.store$.dispatch(OrganizationActions.getOrganizationUsers());
  }

  public save(): void {
    this.store$.dispatch(
      OrganizationActions.updateActiveOrganization({
        organization: this.organizationForm.value,
      }),
    );
  }


  public cancelInvite(inviteId: string, totalInvitesCount?: number): void {
    this.store$.dispatch(
      InviteActions.cancelInvite({
        inviteId,
      }),
    );
    if (totalInvitesCount === 1) {
      this.addOrganization();
    }
  }

  public acceptInvite(id: string, orgId: string): void {
    this.orgId = orgId;
    this.store$.dispatch(
      InviteActions.acceptInvite({
        id,
      }),
    );
  }

  public addOrganization(): void {
    this.router.navigate([this.step1Url]);
  }

  public changeQuality(quality: Quality) {
    this.organizationForm.patchValue({ videoQuality: quality });
  }

  public get isOrganizationSettingsEditOrganization$() {
    return this.store$.pipe(select(PermissionSelectors.checkAccessPermissionsOR([PermissionModel.Permissions.OrganizationSettingsEditOrganization], [])),
      take(1));
  }
}
